import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

export interface LabEquipmentDetails {
	id: string;
	labName: string;
	numericId: number;
	equipments: Array<{
		equipmentId: string;
		equipmentLabel: string;
		requiredCount: number;
		availableCount: number;
	}>;
}

export interface CourseBuilderFormState {
	// Step 1: Course Details
	courseDetails: {
		courseName: string;
		courseType: string;
		courseLevel: string;
		duration: string;
		totalStudentsCount: number;
		sector: string;
		teachingMode: string;
		courseNotifyingAuthorityId: string;
		description: string;
		overview: string;
	};

	// Step 2: Laboratories
	laboratories: {
		laboratories: LabEquipmentDetails[];
	};

	// Step 3: Faculty Details
	facultyDetails: {
		minimumQualification: string;
		totalFacultyMemberCount: number;
	};

	// Step 4: Attachments
	attachments: {
		attachments: string[];
		fileInfos: {
			objectKey: string;
			name: string;
			size: number;
			type: string;
		}[];
	};

	// UI State
	currentStep: number;
	isFormValid: boolean;
}

const initialState: CourseBuilderFormState = {
	courseDetails: {
		courseName: "",
		courseType: "",
		courseLevel: "",
		duration: "",
		totalStudentsCount: 0,
		sector: "",
		teachingMode: "",
		courseNotifyingAuthorityId: "",
		description: "",
		overview: "",
	},
	laboratories: {
		laboratories: [],
	},
	facultyDetails: {
		minimumQualification: "",
		totalFacultyMemberCount: 0,
	},
	attachments: {
		attachments: [],
		fileInfos: [],
	},
	currentStep: 0,
	isFormValid: false,
};

const courseBuilderSlice = createSlice({
	name: "courseBuilder",
	initialState,
	reducers: {
		// Update specific step data
		updateCourseDetails: (state, action: PayloadAction<Partial<CourseBuilderFormState["courseDetails"]>>) => {
			state.courseDetails = { ...state.courseDetails, ...action.payload };
		},
		updateLaboratories: (state, action: PayloadAction<Partial<CourseBuilderFormState["laboratories"]>>) => {
			state.laboratories = { ...state.laboratories, ...action.payload };
		},
		updateFacultyDetails: (state, action: PayloadAction<Partial<CourseBuilderFormState["facultyDetails"]>>) => {
			state.facultyDetails = { ...state.facultyDetails, ...action.payload };
		},
		updateAttachments: (state, action: PayloadAction<Partial<CourseBuilderFormState["attachments"]>>) => {
			state.attachments = { ...state.attachments, ...action.payload };
		},

		// Navigation
		setCurrentStep: (state, action: PayloadAction<number>) => {
			state.currentStep = action.payload;
		},
		nextStep: state => {
			if (state.currentStep < 3) {
				state.currentStep += 1;
			}
		},
		previousStep: state => {
			if (state.currentStep > 0) {
				state.currentStep -= 1;
			}
		},

		// Form validation
		setFormValid: (state, action: PayloadAction<boolean>) => {
			state.isFormValid = action.payload;
		},

		// Reset form (when modal closes or form is submitted)
		resetForm: () => {
			return initialState;
		},

		// Reset specific step
		resetStep: (state, action: PayloadAction<keyof Omit<CourseBuilderFormState, "currentStep" | "isFormValid">>) => {
			const stepKey = action.payload;
			if (stepKey === "courseDetails") {
				state.courseDetails = initialState.courseDetails;
			} else if (stepKey === "laboratories") {
				state.laboratories = initialState.laboratories;
			} else if (stepKey === "facultyDetails") {
				state.facultyDetails = initialState.facultyDetails;
			} else if (stepKey === "attachments") {
				state.attachments = initialState.attachments;
			}
		},
	},
});

export const {
	updateCourseDetails,
	updateLaboratories,
	updateFacultyDetails,
	updateAttachments,
	setCurrentStep,
	nextStep,
	previousStep,
	setFormValid,
	resetForm,
	resetStep,
} = courseBuilderSlice.actions;

export default courseBuilderSlice.reducer;
